import { useState, useRef, useEffect } from "react";
import axios from "axios";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// ── Icons ──
const CloseIcon = () => (
  <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M5 5l10 10M15 5L5 15" />
  </svg>
);
const BellIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
    <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" />
  </svg>
);
const PinIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path d="M12.32 1.07a1 1 0 011.36.19l4.55 5.9a1 1 0 01-.19 1.39l-.01.01-1.7 1.31a2.5 2.5 0 01-2.13.41l-2.96 3.83a5 5 0 01.57 3.26L11.81 17a1 1 0 01-1.57.21L7.2 14.17l-3.72 4.8a.75.75 0 01-1.18-.92l3.72-4.8-2.73-2.11a1 1 0 01.21-1.72l-.01.01.02-.01 2.64-1.02a5 5 0 013.2-.49l3-3.87a2.5 2.5 0 01.52-2.61l.01-.01z" />
  </svg>
);
const TagIcon = () => (
  <svg viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M3 3h6l8 8-6 6-8-8V3zM7 7h.01" />
  </svg>
);
const TextIcon = () => (
  <svg viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M3 5h14M3 10h10M3 15h7" />
  </svg>
);
const TitleIcon = () => (
  <svg viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M4 6h12M4 10h8M4 14h5" />
    <rect x="2" y="3" width="16" height="14" rx="2" />
  </svg>
);

// ── Categories matching typical notice types ──
const CATEGORIES = [
  { value: "General",     label: "General",      emoji: "📋", bg: "#EAF3DE", text: "#3B6D11" },
  { value: "Tender",      label: "Tender",        emoji: "📑", bg: "#ede9fe", text: "#5b21b6" },
  { value: "Recruitment", label: "Recruitment",   emoji: "👥", bg: "#dbeafe", text: "#1e40af" },
  { value: "Policy",      label: "Policy",        emoji: "📜", bg: "#fce7f3", text: "#9d174d" },
  { value: "Holiday",     label: "Holiday",       emoji: "🎉", bg: "#fef9c3", text: "#854d0e" },
  { value: "Urgent",      label: "Urgent",        emoji: "🚨", bg: "#fee2e2", text: "#991b1b" },
];

const CAT_MAP = Object.fromEntries(CATEGORIES.map(c => [c.value, c]));

// ── Live Preview ──
function NoticePreview({ form }) {
  const cat = CAT_MAP[form.category];
  const now = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

  return (
    <div className="rounded-2xl overflow-hidden transition-all duration-300"
      style={{ border: form.isPinned ? "1.5px solid #4ade80" : "1.5px solid #e8f0e0", boxShadow: form.isPinned ? "0 4px 20px rgba(74,222,128,0.12)" : "0 2px 10px rgba(0,0,0,0.04)", background: "white" }}>

      {/* Pinned strip */}
      {form.isPinned && (
        <div className="h-1 w-full" style={{ background: "linear-gradient(to right, #6ee85a, #3dcc40)" }} />
      )}

      <div className="p-4">
        {/* Top row */}
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {cat && (
              <span className="flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full"
                style={{ background: cat.bg, color: cat.text }}>
                {cat.emoji} {cat.label}
              </span>
            )}
            {form.isPinned && (
              <span className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
                style={{ background: "#fef9c3", color: "#92400e" }}>
                📌 Pinned
              </span>
            )}
          </div>
          <span className="text-xs text-gray-400">{now}</span>
        </div>

        {/* Title */}
        <h3 className="font-black text-gray-900 leading-snug mb-2"
          style={{ fontSize: "15px", minHeight: "20px", color: form.title ? "#111" : "#d1d5db" }}>
          {form.title || "Notice title will appear here..."}
        </h3>

        {/* Content preview */}
        <p className="text-gray-500 text-sm leading-relaxed line-clamp-3"
          style={{ minHeight: "60px", color: form.content ? "#6b7280" : "#d1d5db" }}>
          {form.content || "Notice content will appear here. Start typing to see a live preview of how your notice will look to readers..."}
        </p>

        <div className="mt-3 pt-3 flex items-center justify-between" style={{ borderTop: "0.5px solid #f0f5ee" }}>
          <span className="text-xs text-green-600 font-semibold">Read more →</span>
          <span className="text-xs text-gray-400">Viswaraj Ent.</span>
        </div>
      </div>
    </div>
  );
}

// ── Character counter ──
function CharCount({ value, max }) {
  const pct = value.length / max;
  const color = pct > 0.9 ? "#ef4444" : pct > 0.75 ? "#f59e0b" : "#4ade80";
  return (
    <span className="text-xs font-medium" style={{ color }}>
      {value.length}/{max}
    </span>
  );
}


  const ColorWhitelist = [
  "#000000",
  "#ef4444", // red
  "#f59e0b", // yellow
  "#10b981", // green
  "#3b82f6", // blue
  "#6366f1", // indigo
  "#ec4899"  // pink
];

  const modules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    [{ font: [] }],
    [{ size: ["small", false, "large", "huge"] }],

    ["bold", "italic", "underline", "strike"],

    [{ color: ColorWhitelist }, { background: ColorWhitelist }], // 🎨 colors

    [{ script: "sub" }, { script: "super" }],

    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],

    [{ align: [] }],

    ["link", "image", "video"],

    ["clean"]
  ]
};

const formats = [
  "header", "font", "size",
  "bold", "italic", "underline", "strike",
  "color", "background",
  "script",
  "list", "bullet", "indent",
  "align",
  "link", "image", "video"
];


// ── Main Modal ──
export default function CreateNoticeModal({ isOpen, onClose, onSuccess }) {
  const [form, setForm] = useState({ title: "", content: "", category: "", isPinned: false });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const titleRef = useRef();

  useEffect(() => {
    if (isOpen) {
      setForm({ title: "", content: "", category: "", isPinned: false });
      setErrors({});
      setSubmitted(false);
      setShowPreview(false);
      setTimeout(() => titleRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Close on ESC
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape" && isOpen) onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  const handle = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors(e => ({ ...e, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = "Title is required";
    else if (form.title.trim().length < 5) e.title = "Title must be at least 5 characters";
    else if (form.title.length > 200) e.title = "Title cannot exceed 200 characters";
    if (!form.content.trim()) e.content = "Content is required";
    else if (form.content.trim().length < 10) e.content = "Content must be at least 10 characters";
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/notice/create`, form);
      setSubmitted(true);
      onSuccess?.();
    } catch (err) {
      setErrors({ submit: err.response?.data?.message || "Failed to create notice. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)" }}
      onClick={handleClose}
    >
      <div
        className="w-full sm:max-w-2xl max-h-[95vh] overflow-y-auto rounded-t-3xl sm:rounded-2xl bg-white"
        style={{ fontFamily: "'Barlow', 'Montserrat', sans-serif" }}
        onClick={e => e.stopPropagation()}
      >

        {/* ── Header ── */}
        <div
          className="sticky top-0 z-10 px-6 py-5"
          style={{ background: "linear-gradient(135deg, #0d2d0d 0%, #1a4a1a 100%)", borderRadius: "inherit inherit 0 0" }}
        >
          {/* Dot pattern */}
          <div className="absolute inset-0 pointer-events-none rounded-t-2xl"
            style={{ backgroundImage: "radial-gradient(circle, rgba(74,222,128,0.08) 1px, transparent 1px)", backgroundSize: "20px 20px" }} />

          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: "linear-gradient(135deg, #6ee85a, #3dcc40)", boxShadow: "0 4px 12px rgba(74,222,128,0.4)" }}>
                <BellIcon />
              </div>
              <div>
                <h2 className="text-white font-black text-base leading-none">Create Notice</h2>
                <p className="text-green-400 text-xs mt-0.5">Post an official announcement</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Preview toggle */}
              <button
                onClick={() => setShowPreview(p => !p)}
                className="text-xs font-bold px-3 py-1.5 rounded-lg transition-all"
                style={{
                  background: showPreview ? "rgba(74,222,128,0.2)" : "rgba(255,255,255,0.1)",
                  color: showPreview ? "#4ade80" : "rgba(255,255,255,0.7)",
                  border: showPreview ? "1px solid rgba(74,222,128,0.4)" : "1px solid rgba(255,255,255,0.15)",
                }}>
                {showPreview ? "✓ Preview on" : "Preview"}
              </button>
              {!loading && (
                <button onClick={handleClose} className="text-white/60 hover:text-white transition-colors p-1">
                  <CloseIcon />
                </button>
              )}
            </div>
          </div>
        </div>

        {submitted ? (
          /* ── Success State ── */
          <div className="flex flex-col items-center text-center px-8 py-14 gap-5">
            <div className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #6ee85a, #3dcc40)", boxShadow: "0 8px 28px rgba(74,222,128,0.4)" }}>
              <svg viewBox="0 0 32 32" width="32" height="32" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                <path d="M6 16l8 8L26 8" />
              </svg>
            </div>
            <div>
              <h3 className="font-black text-gray-900 text-xl mb-2">Notice Published!</h3>
              <p className="text-gray-500 text-sm leading-relaxed max-w-xs">
                <strong className="text-gray-800">"{form.title}"</strong> has been published successfully
                {form.isPinned && " and pinned to the top"}.
              </p>
            </div>
            <div className="flex gap-3 mt-2">
              <button
                onClick={() => { setForm({ title: "", content: "", category: "", isPinned: false }); setSubmitted(false); }}
                className="px-5 py-2.5 rounded-xl text-sm font-bold border transition-all hover:bg-gray-50"
                style={{ borderColor: "#e0ead8", color: "#374151" }}>
                Create Another
              </button>
              <button
                onClick={handleClose}
                className="px-5 py-2.5 rounded-xl text-sm font-black transition-all hover:opacity-90"
                style={{ background: "linear-gradient(135deg, #6ee85a, #3dcc40)", color: "#0d2d0d" }}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <div className={`grid gap-0 ${showPreview ? "sm:grid-cols-2" : "grid-cols-1"}`}>

            {/* ── Form ── */}
            <div className="px-6 py-6 flex flex-col gap-5">

              {/* Title */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="flex items-center gap-1.5 text-xs font-black text-gray-700 uppercase tracking-wider">
                    <span style={{ color: "#3B6D11" }}><TitleIcon /></span>
                    Title <span className="text-red-400">*</span>
                  </label>
                  <CharCount value={form.title} max={200} />
                </div>
                <input
                  ref={titleRef}
                  name="title"
                  value={form.title}
                  onChange={handle}
                  placeholder="e.g. Annual Maintenance Shutdown — April 2026"
                  maxLength={200}
                  className="w-full text-sm px-4 py-3 rounded-xl outline-none transition-all"
                  style={{
                    border: `1.5px solid ${errors.title ? "#fca5a5" : form.title ? "#4ade80" : "#e5e7eb"}`,
                    background: errors.title ? "#fff5f5" : form.title ? "#f0fdf4" : "#fafafa",
                    fontFamily: "inherit",
                    color: "#111",
                  }}
                  onFocus={e => { if (!errors.title) e.target.style.borderColor = "#4ade80"; }}
                  onBlur={e => { if (!errors.title && !form.title) e.target.style.borderColor = "#e5e7eb"; }}
                />
                {errors.title && (
                  <p className="flex items-center gap-1 text-red-400 text-xs mt-1.5">
                    <svg viewBox="0 0 12 12" width="11" height="11" fill="currentColor"><path d="M6 1a5 5 0 100 10A5 5 0 006 1zm0 3a.5.5 0 01.5.5v2a.5.5 0 01-1 0v-2A.5.5 0 016 4zm0 4.5a.5.5 0 110 1 .5.5 0 010-1z"/></svg>
                    {errors.title}
                  </p>
                )}
              </div>

              {/* Category */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-black text-gray-700 uppercase tracking-wider mb-2">
                  <span style={{ color: "#3B6D11" }}><TagIcon /></span>
                  Category
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {CATEGORIES.map(cat => (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, category: f.category === cat.value ? "" : cat.value }))}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-bold transition-all"
                      style={{
                        background: form.category === cat.value ? cat.bg : "#fafafa",
                        color: form.category === cat.value ? cat.text : "#6b7280",
                        border: `1.5px solid ${form.category === cat.value ? cat.text + "40" : "#e5e7eb"}`,
                        transform: form.category === cat.value ? "scale(1.02)" : "scale(1)",
                      }}
                    >
                      <span>{cat.emoji}</span>
                      {cat.label}
                      {form.category === cat.value && (
                        <span className="ml-auto">
                          <svg viewBox="0 0 12 12" width="10" height="10" fill={cat.text}>
                            <path d="M2 6l3 3 5-6" stroke={cat.text} strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                          </svg>
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="flex items-center gap-1.5 text-xs font-black text-gray-700 uppercase tracking-wider">
                    <span style={{ color: "#3B6D11" }}><TextIcon /></span>
                    Content <span className="text-red-400">*</span>
                  </label>
                  <CharCount value={form.content} max={2000} />
                </div>
                {/* <textarea
                  name="content"
                  value={form.content}
                  onChange={handle}
                  rows={6}
                  maxLength={2000}
                  placeholder="Write the full notice content here. Include all relevant details such as dates, requirements, and any actions the reader should take..."
                  className="w-full text-sm px-4 py-3 rounded-xl outline-none transition-all"
                  style={{
                    border: `1.5px solid ${errors.content ? "#fca5a5" : form.content ? "#4ade80" : "#e5e7eb"}`,
                    background: errors.content ? "#fff5f5" : form.content ? "#f0fdf4" : "#fafafa",
                    fontFamily: "inherit",
                    resize: "vertical",
                    lineHeight: 1.7,
                    color: "#374151",
                  }}
                  onFocus={e => { if (!errors.content) e.target.style.borderColor = "#4ade80"; }}
                  onBlur={e => { if (!errors.content && !form.content) e.target.style.borderColor = "#e5e7eb"; }}
                /> */}

                <ReactQuill value={form.content} theme="snow"
                            onChange={(value) => {
                            setForm((prev) => ({
                                  ...prev,
                                content: value,
                            }));
                             }}
                            modules={modules}
                            formats={formats}
                            className=""

                          />
                {errors.content && (
                  <p className="flex items-center gap-1 text-red-400 text-xs mt-1.5">
                    <svg viewBox="0 0 12 12" width="11" height="11" fill="currentColor"><path d="M6 1a5 5 0 100 10A5 5 0 006 1zm0 3a.5.5 0 01.5.5v2a.5.5 0 01-1 0v-2A.5.5 0 016 4zm0 4.5a.5.5 0 110 1 .5.5 0 010-1z"/></svg>
                    {errors.content}
                  </p>
                )}
              </div>

              {/* Pin toggle */}
              <div
                className="flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all"
                style={{
                  background: form.isPinned ? "#f0fdf4" : "#fafafa",
                  border: `1.5px solid ${form.isPinned ? "#4ade80" : "#e5e7eb"}`,
                }}
                onClick={() => setForm(f => ({ ...f, isPinned: !f.isPinned }))}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: form.isPinned ? "linear-gradient(135deg, #6ee85a, #3dcc40)" : "#f3f4f6" }}>
                    <span style={{ color: form.isPinned ? "#0d2d0d" : "#9ca3af" }}><PinIcon /></span>
                  </div>
                  <div>
                    <p className="text-sm font-bold" style={{ color: form.isPinned ? "#1a2e0a" : "#374151" }}>
                      Pin this notice
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">Pinned notices appear at the top of the board</p>
                  </div>
                </div>
                {/* Toggle */}
                <div
                  className="relative w-11 h-6 rounded-full transition-colors flex-shrink-0"
                  style={{ background: form.isPinned ? "linear-gradient(135deg, #6ee85a, #3dcc40)" : "#d1d5db" }}
                >
                  <div className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-all"
                    style={{ left: form.isPinned ? "22px" : "2px" }} />
                </div>
              </div>

              {/* API error */}
              {errors.submit && (
                <div className="flex items-center gap-2 p-3 rounded-xl text-sm"
                  style={{ background: "#fff5f5", border: "1px solid #fca5a5", color: "#dc2626" }}>
                  <svg viewBox="0 0 16 16" width="16" height="16" fill="currentColor">
                    <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 3a.5.5 0 01.5.5v3a.5.5 0 01-1 0v-3A.5.5 0 018 4zm0 6a.75.75 0 110 1.5.75.75 0 010-1.5z"/>
                  </svg>
                  {errors.submit}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-1">
                <button
                  onClick={handleClose}
                  disabled={loading}
                  className="flex-1 py-3 rounded-xl text-sm font-semibold border transition-all hover:bg-gray-50"
                  style={{ borderColor: "#e0ead8", color: "#6b7280" }}>
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading || !form.title.trim() || !form.content.trim()}
                  className="flex-1 py-3 rounded-xl text-sm font-black transition-all flex items-center justify-center gap-2"
                  style={{
                    background: loading || !form.title.trim() || !form.content.trim()
                      ? "#d1fae5"
                      : "linear-gradient(135deg, #6ee85a 0%, #3dcc40 100%)",
                    color: "#0d2d0d",
                    cursor: loading || !form.title.trim() || !form.content.trim() ? "not-allowed" : "pointer",
                    boxShadow: loading || !form.title.trim() || !form.content.trim() ? "none" : "0 4px 16px rgba(74,222,128,0.35)",
                  }}>
                  {loading ? (
                    <><div className="w-4 h-4 rounded-full border-2 border-green-800 border-t-transparent animate-spin" /> Publishing...</>
                  ) : (
                    <><span>📢</span> Publish Notice</>
                  )}
                </button>
              </div>
            </div>

            {/* ── Live Preview Panel ── */}
            {showPreview && (
              <div className="px-5 py-6 border-l" style={{ borderColor: "#f0f5ee", background: "#f8faf6" }}>
                <p className="text-xs font-black text-gray-500 uppercase tracking-widest mb-4">Live Preview</p>
                <NoticePreview form={form} />
                <p className="text-xs text-gray-400 mt-3 text-center">Updates as you type</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}